import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { productContentTranslations } from './productContentTranslations';
import { legalTranslations } from './legalTranslations';
import { croatianTranslations } from './translations/hr';
import { finnishTranslations } from './translations/fi';
import { frenchTranslations } from './translations/fr';
import { germanTranslations } from './translations/de';
import { norwegianTranslations } from './translations/no';
import { spanishTranslations } from './translations/es';
import { arabicTranslations } from './translations/ar';

export type Language = 'en-US' | 'en-GB' | 'en-AU' | 'he' | 'hr' | 'fi' | 'fr' | 'de' | 'no' | 'es' | 'ar';
export type Currency = 'USD' | 'GBP' | 'AUD' | 'ILS' | 'CAD' | 'EUR' | 'NOK' | 'AED';

const RTL_LANGUAGES: Language[] = ['he', 'ar'];
export const isRTL = (lang: Language): boolean => RTL_LANGUAGES.includes(lang);

export const languages: { code: Language; name: string }[] = [
  { code: 'en-US', name: 'EN (US)' },
  { code: 'en-GB', name: 'EN (UK)' },
  { code: 'en-AU', name: 'EN (AU)' },
  { code: 'he', name: 'עברית' },
  { code: 'hr', name: 'Hrvatski' },
  { code: 'fi', name: 'Suomi' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'no', name: 'Norsk' },
  { code: 'es', name: 'Español' },
  { code: 'ar', name: 'العربية' },
];

export const currencies: { code: Currency; symbol: string; name: string }[] = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
  { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
];

// Country to language/currency mapping
const countryToLocale: Record<string, { language: Language; currency: Currency }> = {
  US: { language: 'en-US', currency: 'USD' },
  CA: { language: 'en-US', currency: 'CAD' },
  GB: { language: 'en-GB', currency: 'GBP' },
  AU: { language: 'en-AU', currency: 'AUD' },
  IL: { language: 'he', currency: 'ILS' },
  HR: { language: 'hr', currency: 'EUR' },
  FI: { language: 'fi', currency: 'EUR' },
  FR: { language: 'fr', currency: 'EUR' },
  DE: { language: 'de', currency: 'EUR' },
  NO: { language: 'no', currency: 'NOK' },
  ES: { language: 'es', currency: 'EUR' },
  AE: { language: 'ar', currency: 'AED' },
};

// ── Base English translations (shared by en-US, en-GB, en-AU) ────────────────
const baseEnglish: Record<string, string> = {
  // Navigation
  'nav.home': 'Home',
  'nav.shop': 'Our Products',
  'nav.about': 'About',
  'nav.contact': 'Contact',
  'nav.blog': 'Blog',
  'nav.trackOrder': 'Track My Order',

  // Hero
  'hero.badge.pdrn': 'PDRN Technology',
  'hero.badge.glassSkin': 'Glass Skin Effect',
  'hero.badge.korean': 'Korean Innovation',
  'hero.title': 'PDRN Skincare — Korean Salmon DNA',
  'hero.subtitle': 'Transform your skin with our clinically proven PDRN serum, sheet masks & capsule cream. Achieve the coveted glass skin effect with premium PDRN skincare products powered by salmon DNA technology.',
  'hero.cta': 'Explore Collection',
  'hero.ctaSurvey': 'Take Our Skin Quiz',
  'hero.socialProof': 'Loved by 10,000+ skincare enthusiasts',
  'survey.bubbleTooltip': 'Get your personalized routine + 10% off',

  // Products
  'products.title': 'Our Collection',
  'products.subtitle': 'Premium Korean skincare powered by PDRN technology',
  'products.addToCart': 'Add to Cart',
  'products.buyNow': 'Buy Now',
  'products.soldOut': 'Sold Out',
  'products.save': 'Sale',
  'products.viewDetails': 'View Details',
  'products.noProducts': 'No products available at the moment',
  'products.selectSize': 'Select Size',
  'products.quantity': 'Quantity',
  'products.inStock': 'In Stock',
  'products.outOfStock': 'Out of Stock',

  // Upsell
  'upsell.title': 'Complete Your Routine',
  'upsell.savings': 'Save with the complete set',
  'upsell.cta': 'Upgrade to Full Set',
  'upsell.includes': 'Complete skincare routine in one package',
  'upsell.badge': 'Complete Your Routine',
  'upsell.saveBadge': 'Save {amount}!',
  'upsell.whyBundle': 'Why choose the complete set?',
  'upsell.whyBundleDesc': 'Each step builds on the last for maximum results',
  'upsell.includesSerum': 'Pink Glow Serum',
  'upsell.includesCream': 'Smart Capsule Cream',
  'upsell.includesMasks': '10-Pack Sheet Masks',
  'upsell.separatelyPrice': 'Separately: {amount}',
  'upsell.serumTooltip': 'Lightweight PDRN serum that boosts collagen, smooths wrinkles, and delivers instant Glass Skin glow.',
  'upsell.creamTooltip': 'Innovative capsule formula with PDRN, 5% Niacinamide, and Hydrolyzed Collagen for deep hydration and firmness.',
  'upsell.masksTooltip': '10 sheet masks soaked in concentrated PDRN serum for an intensive treatment that delivers instant Glass Skin effect.',

  // Cart
  'cart.title': 'Shopping Cart',
  'cart.empty': 'Your cart is empty',
  'cart.itemCount': '{count} item(s) in your cart',
  'cart.total': 'Total',
  'cart.checkout': 'Checkout',
  'cart.continueShopping': 'Continue Shopping',
  'cart.addedToCart': 'Added to cart!',
  'cart.couponPlaceholder': 'Discount code',
  'cart.applyCoupon': 'Apply',
  'cart.couponApplied': 'Discount applied!',
  'cart.couponError': 'Invalid code',
  'cart.removeCoupon': 'Remove',
  'cart.freeShippingUnlocked': "You've unlocked FREE shipping!",
  'cart.freeShippingRemaining': 'Add {amount} more for FREE shipping',
  'cart.freeShippingThreshold': 'Free shipping on orders over {amount}',
  'cart.completeRoutine': 'Complete Your Routine',
  'cart.bundleSave': 'Save {percent}%',
  'cart.quickAdd': 'Add',

  // Promo
  'promo.b2g1': '🎁 Add 3 items to cart — get 1 FREE!',
  'promo.b2g1Cart': '🎁 Buy 2 Get 1 FREE applied!',
  'promo.b2g1CartHint': 'Add {count} more to get a FREE product! (3 items needed)',
  'promo.b2g1Saving': 'You save {amount} — cheapest item is FREE!',
  'promo.freeItem': '🎁 FREE in promo',

  // About
  'about.title': 'About Us',
  'about.subtitle': 'The science behind Korean beauty',
  'about.pdrnTitle': 'The Science of PDRN Skincare',
  'about.pdrnDescription': 'PDRN (Polydeoxyribonucleotide) is derived from salmon DNA — a revolutionary ingredient in Korean skincare. Our PDRN skincare products promote cellular regeneration for the ultimate glass skin effect.',
  'about.glassSkinDesc': 'Achieve that coveted Korean glass skin effect with instant radiance and luminosity.',
  'about.hydrationDesc': 'Deep hydration that lasts all day, with clinically proven moisture retention.',
  'about.antiAgingDesc': 'Boost collagen production naturally for firmer, more youthful-looking skin.',

  // Footer
  'footer.copyright': '© 2026 PDRN SKIN. All rights reserved.',
  'footer.freeShipping': 'Free Shipping over {amount}',
  'footer.securePayment': 'Secure Payment',
  'footer.support': '24/7 Support',
  'footer.description': 'Premium Korean skincare powered by revolutionary PDRN technology. Experience the glass skin effect with our scientifically-formulated products.',
  'footer.sheetMasks': 'Sheet Masks',
  'footer.serum': 'Serum',
  'footer.cream': 'Cream',
  'footer.bundle': 'Complete Bundle',
  'footer.faq': 'FAQ',
  'footer.shippingReturns': 'Shipping & Returns',
  'footer.privacyPolicy': 'Privacy Policy',
  'footer.termsOfService': 'Terms of Service',
  'footer.returnPolicy': 'Return Policy',
  'footer.contactSupport': 'We\'re here for you — questions, order status, issues, or help with a purchase. Reach our customer support at',

  // Common UI
  'common.verified': 'Verified',
  'common.before': 'Before',
  'common.after': 'After',
  'common.loading': 'Loading...',
  'common.error': 'An error occurred',
  'common.noPhotoShared': 'Customer did not attach a photo',

  // Media placeholders
  'media.videoPlaceholder': 'Video placeholder',
  'media.gifPlaceholder': 'GIF placeholder',
  'media.imagePlaceholder': 'Image placeholder',

  // Product features
  'feature.hydration': '+100% Hydration',
  'feature.glassSkin': 'Glass Skin Effect',
  'feature.antiAging': 'Anti-Aging',
  'feature.collagen': 'Collagen Boost',
  'feature.brightening': 'Brightening',

  // Product page
  'product.howToUse': 'How to Use',
  'product.ingredients': 'Key Ingredients',
  'product.reviews': 'Customer Reviews',
  'product.results': 'Real Results',
  'product.realResults': 'Real Results',
  'product.beforeAfter': 'Before & After',
  'product.faqTitle': 'Frequently Asked Questions',
  'product.mediaGallery': 'Media Gallery',
  'product.freeShipping': 'Free Shipping {amount}+',
  'product.secureCheckout': 'Secure Checkout',
  'product.returns': '7-Day Returns',
  'product.koreanFormula': 'Korean Formula',
  'product.premiumIngredients': 'Premium Ingredients',
  'product.backHome': 'Back to Home',
  'product.notFound': 'Product not found',
  'product.bestValue': 'Best Value',
  'product.bestseller': 'Bestseller',
  'product.reviewCount': '({count} reviews)',
  'product.crueltyFree': 'Cruelty Free',
  'product.youMayAlsoLike': 'You May Also Like',
  'product.completeRoutine': 'Complete your skincare routine',
  'product.moneyBack': '7-Day Money Back',
  'product.dermatologistTested': 'Dermatologist Tested',
  'product.payWith': 'Pay securely with',
  'product.specsTitle': 'Product Details',
  'product.specsVolume': 'Size',
  'product.specsSkinType': 'Skin Type',
  'product.specsTested': 'Testing',
  'product.specsClinicallyTested': 'Clinically Tested',
  'product.specsShelfLife': 'Shelf Life',
  'product.specsFreeOf': 'Free of parabens, sulfates & phthalates',
  'product.satisfactionGuarantee': '100% Satisfaction Guarantee',
  'product.satisfactionDesc': 'Try risk-free with our 7-day money-back guarantee',

  // Before/After
  'beforeAfter.customer1': 'Hannah',
  'beforeAfter.customer2': 'Anita Nayar',
  'beforeAfter.customer3': 'LaTanisha',
  'beforeAfter.weeks3': '3 weeks of use',
  'beforeAfter.weeks8': '8 weeks of use',
  'beforeAfter.weeks12': '12 weeks of use',

  // Mobile sticky cart
  'mobileCart.findRoutine': 'Find My Routine',

  // Survey
  'survey.buttonText': 'Find My Routine',
  'survey.title': 'Skin Analysis',
  'survey.subtitle': 'Answer a few questions to get personalized product recommendations',
  'survey.question': 'Question',
  'survey.back': 'Back',
  'survey.next': 'Next',
  'survey.getResults': 'Get My Results',
  'survey.resultsTitle': 'Your Personalized Routine',
  'survey.resultsSubtitle': 'Based on your skin profile, here is our recommendation',
  'survey.yourProfile': 'Your Skin Profile',
  'survey.recommended': 'Recommended For You',
  'survey.bundleNote': 'For best results, we recommend the complete bundle',
  'survey.howToUse': 'Your Personalized Routine',
  'survey.morning': 'Morning',
  'survey.evening': 'Evening',
  'survey.expectedResults': 'Expected Results',
  'survey.week': 'Week',
  'survey.weeks': 'Weeks',
  'survey.personalTips': 'Tips For You',
  'survey.retake': 'Retake Survey',
  'survey.shopNow': 'Shop Now',
  'survey.errorTooMany': 'Too many requests. Please try again later.',
  'survey.errorGeneric': 'Something went wrong. Please try again.',
  'survey.yourPersonalizedRoutine': 'Your Personalized Routine',
  'survey.treatmentPlan': 'Recommended Treatment Duration',
  'survey.addRoutineToCart': 'Add My Routine to Cart',
  'survey.satisfactionGuarantee': '100% Satisfaction Guarantee',
  'survey.maybeLater': 'Maybe Later',
  'survey.continueSurvey': 'Continue Survey',
  'survey.addedToCart': 'Products added to cart!',
  'survey.personalizedRoutine': 'Your personalized routine is ready',
  'survey.errorAddingToCart': 'Error adding products to cart',
  'survey.exclusiveDiscount': 'Your Exclusive 10% Off!',
  'survey.couponCopied': 'Coupon code copied!',
  'survey.useAtCheckout': 'Use code SURVEY10 at checkout for 10% off',
  'survey.bestDeal': 'Best Deal For You!',
  'survey.separatePrice': 'Separately',
  'survey.yourPrice': 'Your Price',
  'survey.youSave': 'You Save',
  'survey.upgradeOption': 'Want even better results?',
  'survey.upgradeToBundle': 'Upgrade to Full Routine',
  'survey.upgradeSave': 'Save {amount} with the complete set',
  'survey.addSingleToCart': 'Add to Cart',

  // Survey loading messages
  'survey.loading.analyzing': 'Analyzing your skin profile...',
  'survey.loading.planning': 'Creating your personalized plan...',
  'survey.loading.matching': 'Finding the best products for you...',
  'survey.loading.finalizing': 'Almost ready...',

  // Survey Questions
  'survey.q1.question': 'What is your skin type?',
  'survey.q1.dry': 'Dry - My skin often feels tight',
  'survey.q1.oily': 'Oily - Shiny, especially in T-zone',
  'survey.q1.combination': 'Combination - Oily T-zone, dry cheeks',
  'survey.q1.normal': 'Normal - Balanced, no major issues',
  'survey.q2.question': 'What is your main skin concern?',
  'survey.q2.aging': 'Fine lines & wrinkles',
  'survey.q2.acne': 'Acne & breakouts',
  'survey.q2.dullness': 'Dullness & uneven skin tone',
  'survey.q2.hyperpigmentation': 'Dark spots & hyperpigmentation',
  'survey.q3.question': 'How sensitive is your skin?',
  'survey.q3.very': 'Very sensitive - Reacts to most products',
  'survey.q3.somewhat': 'Somewhat sensitive - Occasional reactions',
  'survey.q3.not': 'Not sensitive - Can tolerate most products',
  'survey.q4.question': 'How hydrated does your skin feel?',
  'survey.q4.veryDry': 'Very dry - Constantly needs moisture',
  'survey.q4.somewhatDry': 'Somewhat dry - Needs regular hydration',
  'survey.q4.balanced': 'Balanced - Neither dry nor oily',
  'survey.q4.wellHydrated': 'Well hydrated - Plump and healthy',
  'survey.q5.question': 'What is your age range?',
  'survey.q5.under25': 'Under 25',
  'survey.q5.25to35': '25-35',
  'survey.q5.35to45': '35-45',
  'survey.q5.over45': 'Over 45',
  'survey.q6.question': 'How would you describe your current skincare routine?',
  'survey.q6.minimal': 'Minimal - Just cleanser and moisturizer',
  'survey.q6.basic': 'Basic - Cleanser, toner, moisturizer',
  'survey.q6.moderate': 'Moderate - Including serums and treatments',
  'survey.q6.extensive': 'Extensive - Full K-beauty routine',
  'survey.q7.question': 'How would you describe your skin texture?',
  'survey.q7.smooth': 'Smooth and even',
  'survey.q7.rough': 'Rough and textured',
  'survey.q7.uneven': 'Uneven with some bumps',
  'survey.q7.bumpy': 'Bumpy with visible pores',
  'survey.q8.question': 'What is your main skincare goal?',
  'survey.q8.glow': 'Achieve glass skin glow',
  'survey.q8.antiAging': 'Reduce signs of aging',
  'survey.q8.hydration': 'Deep hydration',
  'survey.q8.all': 'All of the above',

  // Trust Strip
  'trust.clinicallyTested': 'Clinically Tested',
  'trust.happyCustomers': '10,000+ Happy Customers',
  'trust.labFormula': 'Korean Lab Formula',
  'trust.moneyBack': '7-Day Money Back',

  // Science section
  'science.title': 'PDRN Salmon DNA — Backed by Science',
  'science.subtitle': 'PDRN is clinically proven with peer-reviewed research',
  'science.citation1': '"PDRN promotes wound healing and tissue regeneration through adenosine A2A receptor activation."',
  'science.source1': '— Journal of Cosmetic Dermatology, 2020',
  'science.citation2': '"Salmon-derived PDRN significantly improves skin hydration, elasticity, and reduces wrinkle depth in clinical trials."',
  'science.source2': '— International Journal of Molecular Sciences, 2021',

  // What is PDRN (educational section)
  'whatIsPDRN.title': 'What is PDRN?',
  'whatIsPDRN.subtitle': 'The revolutionary ingredient transforming skincare — from clinic to home',
  'whatIsPDRN.origin.title': 'Derived from Salmon DNA',
  'whatIsPDRN.origin.subtitle': 'A proven ingredient in aesthetic medicine',
  'whatIsPDRN.origin.point1': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics worldwide',
  'whatIsPDRN.origin.point1.US': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across the United States',
  'whatIsPDRN.origin.point1.GB': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across the United Kingdom',
  'whatIsPDRN.origin.point1.AU': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Australia',
  'whatIsPDRN.origin.point1.IL': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Israel',
  'whatIsPDRN.origin.point1.FR': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across France',
  'whatIsPDRN.origin.point1.DE': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Germany',
  'whatIsPDRN.origin.point1.ES': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Spain',
  'whatIsPDRN.origin.point1.NO': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Norway',
  'whatIsPDRN.origin.point1.HR': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Croatia',
  'whatIsPDRN.origin.point1.FI': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across Finland',
  'whatIsPDRN.origin.point1.AE': 'PDRN (Polydeoxyribonucleotide) is extracted from salmon DNA — a biocompatible molecule used in dermatology clinics across the UAE',
  'whatIsPDRN.origin.point2': 'Backed by peer-reviewed clinical research with measurable results on hydration and elasticity',
  'whatIsPDRN.origin.point3': 'A natural regenerative compound — no synthetic fillers, no harsh chemicals',
  'whatIsPDRN.howItWorks.title': 'Cell-Level Regeneration',
  'whatIsPDRN.howItWorks.subtitle': 'Works where other products can\'t reach',
  'whatIsPDRN.howItWorks.point1': 'Activates the A2A adenosine receptor — triggering your skin\'s natural repair and renewal process',
  'whatIsPDRN.howItWorks.point2': 'Promotes collagen and elastin production from within, not just surface hydration',
  'whatIsPDRN.howItWorks.point3': 'Reduces inflammation and calms stressed skin for a healthier, more balanced complexion',
  'whatIsPDRN.results.title': 'Clinical-Grade Results at Home',
  'whatIsPDRN.results.subtitle': 'Previously only available in clinics',
  'whatIsPDRN.results.point1': 'Until recently, PDRN treatments were exclusive to high-end dermatology clinics at premium prices',
  'whatIsPDRN.results.point2': 'Our formulas deliver the same active ingredient in a daily-use format you can apply at home',
  'whatIsPDRN.results.point3': '+100% hydration boost clinically measured — visible improvement from the very first application',

  // Proven Results (statistics strip)
  'provenResults.title': 'Proven Results',
  'provenResults.stat1.value': '+100%',
  'provenResults.stat1.label': 'Hydration Boost',
  'provenResults.stat1.sub': 'Clinically measured',
  'provenResults.stat2.value': '93%',
  'provenResults.stat2.label': 'Smoother Skin',
  'provenResults.stat2.sub': 'Reported within 4 weeks',
  'provenResults.stat3.value': '4.9/5',
  'provenResults.stat3.label': 'Customer Rating',
  'provenResults.stat3.sub': 'Average across all products',
  'provenResults.stat4.value': '10,000+',
  'provenResults.stat4.label': 'Happy Customers',
  'provenResults.stat4.sub': 'Worldwide',
  'provenResults.stat4.sub.US': 'Across the United States',
  'provenResults.stat4.sub.GB': 'Across the United Kingdom',
  'provenResults.stat4.sub.AU': 'Across Australia',
  'provenResults.stat4.sub.IL': 'Across Israel',
  'provenResults.stat4.sub.FR': 'Across France',
  'provenResults.stat4.sub.DE': 'Across Germany',
  'provenResults.stat4.sub.ES': 'Across Spain',
  'provenResults.stat4.sub.NO': 'Across Norway',
  'provenResults.stat4.sub.HR': 'Across Croatia',
  'provenResults.stat4.sub.FI': 'Across Finland',
  'provenResults.stat4.sub.AE': 'Across the UAE',

  // Comparison Table (PDRN vs Regular)
  'comparison.title': 'PDRN vs. Regular Skincare',
  'comparison.headerRegular': 'Regular Skincare',
  'comparison.headerPDRN': 'PDRN SKIN',
  'comparison.row1.criteria': 'Action',
  'comparison.row1.regular': 'Surface-level hydration',
  'comparison.row1.pdrn': 'Cell-level regeneration',
  'comparison.row2.criteria': 'Active Ingredient',
  'comparison.row2.regular': 'Generic peptides',
  'comparison.row2.pdrn': 'PDRN (Salmon DNA)',
  'comparison.row3.criteria': 'Results',
  'comparison.row3.regular': 'Weeks to notice',
  'comparison.row3.pdrn': 'Visible from first use',
  'comparison.row4.criteria': 'Technology',
  'comparison.row4.regular': 'Over-the-counter formulas',
  'comparison.row4.pdrn': 'Clinic-grade technology',
  'comparison.row5.criteria': 'Clinical Evidence',
  'comparison.row5.regular': 'Marketing claims',
  'comparison.row5.pdrn': 'Clinically tested +100% hydration',

  // Science Inside (product section)
  'scienceInside.title': 'The Science Inside',
  'scienceInside.subtitle': 'Why this formula works — two powerful engines in one serum',
  'scienceInside.peptides.title': 'Peptides — The Engineers',
  'scienceInside.peptides.subtitle': '"Smart messengers, not magic cream"',
  'scienceInside.peptides.point1': 'Short amino acid chains that signal your skin to behave like younger, renewing skin',
  'scienceInside.peptides.point2': 'Support a firmer appearance and less visible fine lines over time',
  'scienceInside.peptides.point3': 'Strengthen the skin barrier — skin feels less "fragile" and reactive',
  'scienceInside.peptides.point4': 'A smart effect that builds with consistent use, not a one-day miracle',
  'scienceInside.pdrn.title': 'PDRN — The Restorer',
  'scienceInside.pdrn.subtitle': 'K-Beauty\'s leading restoration ingredient',
  'scienceInside.pdrn.point1': 'DNA fragments that speak the language of renewal and restoration',
  'scienceInside.pdrn.point2': 'Supports skin quality and promotes a healthy, dense appearance',
  'scienceInside.pdrn.point3': 'Calms stressed or reactive skin — less redness, more balance',
  'scienceInside.pdrn.point4': 'In serum form: consistent, smart improvement — not dramatic overnight',
  'scienceInside.combo.title': 'Two Engines, One Serum',
  'scienceInside.combo.subtitle': 'Instant glow + cumulative anti-aging',
  'scienceInside.combo.point1': 'Engine 1: "See it now" — hydration, light diffusion, smoother texture',
  'scienceInside.combo.point2': 'Engine 2: "See it with time" — firming, elasticity, restoration',
  'scienceInside.combo.point3': 'The instant "wow" keeps you consistent — peptides and PDRN do the rest',
  'scienceInside.combo.point4': 'Result: skin that looks dense, calm, and polished',
  'scienceInside.tagline': 'Peptides build and strengthen. PDRN repairs and calms. Together, they give your skin both instant radiance and lasting renewal.',

  // Science Inside — Default (fallback for new products)
  'scienceInside.default.subtitle': 'The science behind PDRN — how it transforms your skin from within',
  'scienceInside.default.tagline': 'PDRN activates your skin\'s natural repair system, promoting cellular regeneration and lasting hydration from the very first use.',

  // Default product FAQ
  'productContent.default.faq1.q': 'What is PDRN and how does it work?',
  'productContent.default.faq1.a': 'PDRN (Polydeoxyribonucleotide) is derived from salmon DNA. It activates your skin\'s natural repair mechanism through the A2A adenosine receptor, promoting cellular regeneration, collagen production, and deep hydration.',
  'productContent.default.faq2.q': 'Is this product suitable for sensitive skin?',
  'productContent.default.faq2.a': 'Yes! Our PDRN formulas are dermatologist tested, cruelty-free, and free of parabens, sulfates, and phthalates. They are designed to be gentle on all skin types, including sensitive skin.',
  'productContent.default.faq3.q': 'How long until I see results?',
  'productContent.default.faq3.a': 'Most users notice improved hydration and a visible glow from the very first use. For best results with fine lines and skin texture, consistent use for 4-8 weeks is recommended.',

  // Science Inside — Masks
  'scienceInside.masks.subtitle': 'How three powerhouse ingredients work together in every sheet mask',
  'scienceInside.masks.tagline': 'PDRN repairs at the cellular level. Vitamin B12 brightens. Niacinamide strengthens. 20 minutes is all it takes for your skin to absorb it all.',
  'scienceInside.masks.card1.title': 'PDRN + Vitamin B12 — The Brightening Duo',
  'scienceInside.masks.card1.subtitle': 'Cellular repair meets visible radiance',
  'scienceInside.masks.card1.point1': 'PDRN activates cell regeneration while Vitamin B12 delivers an instant brightening effect',
  'scienceInside.masks.card1.point2': 'Together they target dullness at the source — not just surface-level glow',
  'scienceInside.masks.card1.point3': 'B12 is a proven skin-conditioning agent that evens out skin tone over time',
  'scienceInside.masks.card1.point4': 'The duo works synergistically — PDRN renews, B12 illuminates',
  'scienceInside.masks.card2.title': 'Niacinamide — The Barrier Builder',
  'scienceInside.masks.card2.subtitle': 'Strengthens, balances, protects',
  'scienceInside.masks.card2.point1': 'Reinforces the skin barrier to lock in moisture and keep irritants out',
  'scienceInside.masks.card2.point2': 'Visibly minimizes pores and regulates excess oil production',
  'scienceInside.masks.card2.point3': 'Reduces redness and calms inflammation — ideal for sensitive skin',
  'scienceInside.masks.card2.point4': 'Works at every skin layer to improve texture, tone, and resilience',
  'scienceInside.masks.card3.title': 'The Mask Effect — 20 Minutes of Deep Delivery',
  'scienceInside.masks.card3.subtitle': 'Why a sheet mask outperforms a regular serum',
  'scienceInside.masks.card3.point1': 'The occlusive sheet creates a sealed environment that prevents evaporation',
  'scienceInside.masks.card3.point2': 'Active ingredients are delivered at a higher concentration than daily serums',
  'scienceInside.masks.card3.point3': 'Deep absorption — ingredients penetrate deeper thanks to prolonged skin contact',
  'scienceInside.masks.card3.point4': 'Immediate visible results: plumper, more hydrated, glowing skin after just one use',

  // Science Inside — Cream
  'scienceInside.cream.subtitle': 'Three advanced technologies working overnight to transform your skin',
  'scienceInside.cream.tagline': 'Collagen rebuilds structure. Ceramides protect the barrier. Smart capsules deliver it all through the night — so you wake up with renewed, firmer skin.',
  'scienceInside.cream.card1.title': 'Hydrolyzed Collagen — The Structure',
  'scienceInside.cream.card1.subtitle': 'Rebuilding from the inside out',
  'scienceInside.cream.card1.point1': 'Broken down into smaller fragments that can actually penetrate the skin — not just sit on top',
  'scienceInside.cream.card1.point2': 'Supports the skin\'s structural matrix for improved firmness and elasticity',
  'scienceInside.cream.card1.point3': 'Helps reduce the appearance of fine lines by plumping from within',
  'scienceInside.cream.card1.point4': 'Works with your skin\'s natural collagen production — a boost, not a replacement',
  'scienceInside.cream.card2.title': 'Ceramide Complex — The Shield',
  'scienceInside.cream.card2.subtitle': 'Your skin\'s natural defense, reinforced',
  'scienceInside.cream.card2.point1': 'Ceramides make up 50% of your skin\'s natural barrier — we replenish what\'s lost with age',
  'scienceInside.cream.card2.point2': 'Locks in hydration for up to 72 hours — no more dry, tight-feeling skin overnight',
  'scienceInside.cream.card2.point3': 'Protects against environmental damage: pollution, dry air, temperature changes',
  'scienceInside.cream.card2.point4': 'Strengthens the barrier so other active ingredients can work more effectively',
  'scienceInside.cream.card3.title': 'Smart Capsule Technology — Timed Release',
  'scienceInside.cream.card3.subtitle': 'Active ingredients delivered all night long',
  'scienceInside.cream.card3.point1': 'Micro-capsules break down gradually, releasing active ingredients over 8 hours',
  'scienceInside.cream.card3.point2': 'Prevents ingredient degradation — actives stay potent until the moment they\'re released',
  'scienceInside.cream.card3.point3': 'Your skin receives a steady stream of nutrition instead of one big dose that fades',
  'scienceInside.cream.card3.point4': 'Wake up to visibly renewed, hydrated skin — the capsules did the work while you slept',

  // Science Inside — Bundle
  'scienceInside.bundle.subtitle': 'Why three products together deliver results no single product can match',
  'scienceInside.bundle.tagline': 'Masks cleanse and infuse. The serum repairs and illuminates. The cream locks it all in. Three steps, one system — 24 hours of continuous skin transformation.',
  'scienceInside.bundle.card1.title': 'Three Steps, One System',
  'scienceInside.bundle.card1.subtitle': 'Each product prepares the skin for the next',
  'scienceInside.bundle.card1.point1': 'Step 1: Sheet masks cleanse deeply and infuse concentrated PDRN + Vitamin B12',
  'scienceInside.bundle.card1.point2': 'Step 2: The serum repairs, firms, and delivers the Glass Skin glow effect',
  'scienceInside.bundle.card1.point3': 'Step 3: The cream seals everything in with collagen, ceramides, and timed-release capsules',
  'scienceInside.bundle.card1.point4': 'Together: a complete routine where each step amplifies the one before it',
  'scienceInside.bundle.card2.title': 'The Synergy Effect — Better Together',
  'scienceInside.bundle.card2.subtitle': 'Cumulative results that multiply over time',
  'scienceInside.bundle.card2.point1': 'Using all three products creates a compounding effect — results build on each other daily',
  'scienceInside.bundle.card2.point2': 'PDRN in every step means consistent cell-level renewal throughout your routine',
  'scienceInside.bundle.card2.point3': 'Different delivery methods (mask, serum, cream) ensure ingredients reach every skin layer',
  'scienceInside.bundle.card2.point4': 'Customers report noticeably faster results with the full routine vs. single products',
  'scienceInside.bundle.card3.title': '24-Hour Coverage — Day & Night',
  'scienceInside.bundle.card3.subtitle': 'Your skin is never left unprotected',
  'scienceInside.bundle.card3.point1': 'Morning: serum under makeup for all-day hydration, protection, and glow',
  'scienceInside.bundle.card3.point2': 'Evening: sheet mask for deep cleansing and infusion (2-3 times per week)',
  'scienceInside.bundle.card3.point3': 'Night: cream with timed-release capsules working while you sleep',
  'scienceInside.bundle.card3.point4': 'Result: round-the-clock renewal — your skin improves 24/7, not just when you remember',

  // FAQ
  'faq.sectionTitle': 'Frequently Asked Questions',

  // 404
  '404.title': 'Page Not Found',
  '404.subtitle': 'The page you\'re looking for doesn\'t exist or has been moved.',
  '404.backHome': 'Return to Home',
  '404.popularProducts': 'Popular Products',

  // Product disclaimer
  'product.disclaimer': '*Images are for illustration purposes only. Results may vary from person to person depending on skin type and usage. This product is not intended to diagnose, treat, or prevent any medical condition. We recommend performing a patch test on a small, hidden area before first full use. If irritation occurs, discontinue use immediately. If irritation is severe, consult a physician. The supplier may change product packaging and appearance from time to time; the product itself remains the same.',
  'product.shippingMarquee': 'Free Shipping on Orders Over {amount}',
  'product.inStockReady': 'In Stock - Ready to Ship',

  // Terms agreement
  'product.termsAgreementBefore': 'By browsing and purchasing on this site you agree to the',
  'product.termsLink': 'Terms of Use',
  'product.termsAgreementAfter': '',

  // Announcement — generic (no country name)
  'announcement.freeShipping': '✨ Free Shipping on Orders Over {amount}',
  'announcement.close': 'Close',

  // Urgency
  'urgency.limitedOffer': 'Limited Offer',
  'urgency.endsIn': 'Ends in',

  // Blog
  'blog.title': 'Blog',
  'blog.subtitle': 'Expert skincare guides, PDRN science, and Korean beauty tips',
  'blog.metaDescription': 'Read our expert guides on PDRN skincare, salmon DNA science, glass skin routines, and Korean beauty tips. Learn how PDRN transforms your skin.',
  'blog.readMore': 'Read More',
  'blog.minRead': 'min read',
  'blog.badge': 'Science-Backed Skincare',
  'blog.chip.antiAging': 'Anti-Aging',
  'blog.chip.hydration': 'Hydration',
  'blog.chip.sensitiveSkin': 'Sensitive Skin',
  'blog.chip.nightRoutine': 'Night Routine',
  'blog.chip.ingredients': 'Ingredients',
  'blog.chip.layering': 'Layering',
  'blog.chip.glassSkin': 'Glass Skin',
  'blog.chip.pdrnScience': 'PDRN Science',
  'blog.relatedArticles': 'Related Articles',
  'blog.recommendedProducts': 'Recommended Products',
  'blog.latestArticles': 'Latest from Our Blog',
  'blog.latestSubtitle': 'Expert PDRN skincare guides and Korean beauty tips',
  'blog.viewAll': 'View All Articles',
  'blog.statsArticles': 'Articles',
  'blog.statsLanguages': 'Languages',
  'blog.statsVersions': 'Translated Versions',
  'blog.availableIn': 'Available in 11 Languages',
  'blog.avgReadTime': 'Avg 5 min read',

  // SEO meta (homepage)
  'seo.homeTitle': 'PDRN Skincare | Korean Salmon DNA Serum, Masks & Cream | Glass Skin',
  'seo.homeDescription': 'Shop PDRN skincare powered by Korean salmon DNA technology. Buy PDRN serum, sheet masks & capsule cream for the glass skin effect. Clinically proven with free shipping.',

  // SEO product titles & descriptions (localized for search engines)
  'seo.productTitle.pink-glow-serum-with-pdrn-peptides': 'PDRN Serum with Salmon DNA & Peptides | PDRN Skincare',
  'seo.productDescription.pink-glow-serum-with-pdrn-peptides': 'Buy PDRN serum with salmon DNA and peptides. Korean glass skin serum that boosts collagen, smooths wrinkles, and delivers instant glow. Shop PDRN skincare with free shipping.',
  'seo.productTitle.pdrn-pink-sheet-masks-10-pack': 'PDRN Sheet Masks 10-Pack | Korean Salmon DNA Masks | PDRN Skincare',
  'seo.productDescription.pdrn-pink-sheet-masks-10-pack': 'Buy PDRN sheet masks soaked in salmon DNA serum. 10-pack Korean face masks for glass skin effect with PDRN, Vitamin B12 & Niacinamide. Free shipping on PDRN skincare.',
  'seo.productTitle.smart-capsule-cream-with-collagen': 'PDRN Capsule Cream with Collagen | Korean Skincare | PDRN Skincare',
  'seo.productDescription.smart-capsule-cream-with-collagen': 'Buy PDRN capsule cream with collagen and niacinamide. Korean anti-aging cream with salmon DNA technology for deep hydration and firmness. Shop PDRN skincare.',
  'seo.productTitle.complete-pdrn-trio-bundle': 'Complete PDRN Skincare Set — Serum, Masks & Cream | PDRN Skincare',
  'seo.productDescription.complete-pdrn-trio-bundle': 'Buy the complete PDRN skincare bundle with serum, 10 sheet masks & capsule cream. Save up to 53% on Korean salmon DNA skincare. Free shipping included.',

  // Internal linking anchor text
  'seo.explorePDRN': 'Explore PDRN Skincare',
  'seo.anchor.serumBenefits': 'PDRN Serum Benefits',
  'seo.anchor.howPDRNWorks': 'How PDRN Works',
  'seo.anchor.masksVsTraditional': 'PDRN Masks vs Traditional',
  'seo.anchor.glassSkinGuide': 'Glass Skin Guide',
  'seo.anchor.koreanSecret': 'Korean Skincare Secret',
  'seo.anchor.pdrnVsRegular': 'PDRN vs Regular Skincare',
};

// ── UK/AU English spelling overrides (shared by en-GB and en-AU) ─────────────
const ukAuSpellingOverrides: Record<string, string> = {
  // "aging" → "ageing"
  'feature.antiAging': 'Anti-Ageing',
  'survey.q8.antiAging': 'Reduce signs of ageing',

  // "personalized" → "personalised"
  'survey.subtitle': 'Answer a few questions to get personalised product recommendations',
  'survey.resultsTitle': 'Your Personalised Routine',
  'survey.yourPersonalizedRoutine': 'Your Personalised Routine',
  'survey.personalizedRoutine': 'Your personalised routine is ready',
  'survey.howToUse': 'Your Personalised Routine',
  'survey.bubbleTooltip': 'Get your personalised routine + 10% off',

  // Survey loading (personalised spelling)
  'survey.loading.planning': 'Creating your personalised plan...',

  // "moisturizer" → "moisturiser"
  'survey.q6.minimal': 'Minimal - Just cleanser and moisturiser',
  'survey.q6.basic': 'Basic - Cleanser, toner, moisturiser',

  // What is PDRN — ageing spelling
  'whatIsPDRN.howItWorks.point3': 'Reduces inflammation and calms stressed skin for a healthier, more balanced complexion',
};

// ── Locale-specific translations ────────────────────────────────────────────
export const translations: Record<Language, Record<string, string>> = {
  'en-US': {
    ...baseEnglish,
    'announcement.freeShipping': '✨ Free Shipping on Orders Over {amount} across the USA',
    'footer.freeShipping': 'Free Shipping over {amount} (USA)',
    'product.shippingMarquee': 'Free Shipping on Orders Over {amount} across the USA',
  },

  'en-GB': {
    ...baseEnglish,
    ...ukAuSpellingOverrides,
    'announcement.freeShipping': '✨ Free Shipping on Orders Over {amount} across the UK',
    'footer.freeShipping': 'Free Shipping over {amount} (UK)',
    'product.shippingMarquee': 'Free Shipping on Orders Over {amount} across the UK',
    'beforeAfter.customer1': 'Charlotte',
    'beforeAfter.customer2': 'Priya Sharma',
    'beforeAfter.customer3': 'Adeola',
    'seo.homeTitle': 'PDRN Skincare UK | Korean Salmon DNA Serum, Masks & Cream | Free UK Delivery',
    'seo.homeDescription': 'Buy PDRN skincare in the UK. Shop Korean salmon DNA serum, sheet masks & capsule cream with free UK delivery. Clinically proven anti-ageing results for glass skin. Prices from £32.',
    'seo.blogTitle': 'PDRN Skincare Blog UK | Korean Beauty Tips, Glass Skin Guides & Expert Advice',
    'seo.blogDescription': 'Read expert UK guides on PDRN skincare, Korean beauty routines, glass skin tips, and salmon DNA science. Free UK delivery on all PDRN products.',
  },

  'en-AU': {
    ...baseEnglish,
    ...ukAuSpellingOverrides,
    'announcement.freeShipping': '✨ Free Shipping on Orders Over {amount} across Australia',
    'footer.freeShipping': 'Free Shipping over {amount} (Australia)',
    'product.shippingMarquee': 'Free Shipping on Orders Over {amount} across Australia',
    'beforeAfter.customer1': 'Tegan',
    'beforeAfter.customer2': 'Priya Singh',
    'beforeAfter.customer3': 'Imani',
  },

  he: {
    'nav.home': 'בית',
    'nav.shop': 'המוצרים שלנו',
    'nav.about': 'אודות',
    'nav.contact': 'צור קשר',
    'nav.blog': 'בלוג',
    'nav.trackOrder': 'מעקב הזמנה',

    'hero.badge.pdrn': 'טכנולוגיית PDRN',
    'hero.badge.glassSkin': 'אפקט Glass Skin',
    'hero.badge.korean': 'חדשנות קוריאנית',
    'hero.title': 'טיפוח PDRN — DNA סלמון קוריאני',
    'hero.subtitle': 'שנו את העור שלכם עם סרום PDRN, מסכות בד וקרם קפסולות מוכחים קלינית. השיגו את אפקט ה-Glass Skin עם מוצרי טיפוח PDRN פרימיום מבוססי DNA סלמון.',
    'hero.cta': 'גלו את הקולקציה',
    'hero.ctaSurvey': 'אבחון עור חינם',
    'hero.socialProof': 'אהובים על ידי 10,000+ חובבות טיפוח',
    'survey.bubbleTooltip': 'שגרה מותאמת אישית + 10% הנחה',

    'products.title': 'הקולקציה שלנו',
    'products.subtitle': 'טיפוח קוריאני פרימיום בטכנולוגיית PDRN',
    'products.addToCart': 'הוסיפו לסל',
    'products.buyNow': 'קנו עכשיו',
    'products.soldOut': 'אזל מהמלאי',
    'products.save': 'מבצע',
    'products.viewDetails': 'לפרטים',
    'products.noProducts': 'אין מוצרים זמינים כרגע',
    'products.selectSize': 'בחרו גודל',
    'products.quantity': 'כמות',
    'products.inStock': 'במלאי',
    'products.outOfStock': 'אזל מהמלאי',

    'upsell.title': 'השלימו את השגרה',
    'upsell.savings': 'חסכו עם הסט המלא',
    'upsell.cta': 'שדרגו לסט מלא',
    'upsell.includes': 'שגרת טיפוח מלאה בחבילה אחת',
    'upsell.badge': 'השלימו את השגרה',
    'upsell.saveBadge': 'חסכו {amount}!',
    'upsell.whyBundle': 'למה לבחור בסט המלא?',
    'upsell.whyBundleDesc': 'כל שלב בונה על הקודם לתוצאות מקסימליות',
    'upsell.includesSerum': 'סרום זוהר ורוד',
    'upsell.includesCream': 'קרם קפסולות חכם',
    'upsell.includesMasks': 'מארז 10 מסכות',
    'upsell.separatelyPrice': 'בנפרד: {amount}',
    'upsell.serumTooltip': 'סרום PDRN קל שמקדם ייצור קולגן, מחליק קמטים ונותן זוהר Glass Skin מיידי.',
    'upsell.creamTooltip': 'פורמולת קפסולות חדשנית עם PDRN, 5% ניאצינמייד וקולגן הידרוליזה ללחות עמוקה ומוצקות.',
    'upsell.masksTooltip': '10 מסכות בד ספוגות בסרום PDRN מרוכז לטיפול אינטנסיבי שנותן אפקט Glass Skin מיידי.',

    'cart.title': 'סל קניות',
    'cart.empty': 'הסל שלכם ריק',
    'cart.itemCount': '{count} פריטים בסל',
    'cart.total': 'סה"כ',
    'cart.checkout': 'לתשלום',
    'cart.continueShopping': 'המשיכו לקנות',
    'cart.addedToCart': 'נוסף לסל!',
    'cart.couponPlaceholder': 'קוד הנחה',
    'cart.applyCoupon': 'החלה',
    'cart.couponApplied': 'ההנחה הוחלה!',
    'cart.couponError': 'קוד לא תקין',
    'cart.removeCoupon': 'הסרה',
    'cart.freeShippingUnlocked': 'קיבלתם משלוח חינם!',
    'cart.freeShippingRemaining': 'הוסיפו עוד {amount} למשלוח חינם',
    'cart.freeShippingThreshold': 'משלוח חינם בהזמנות מעל {amount}',
    'cart.completeRoutine': 'השלימי את השגרה שלך',
    'cart.bundleSave': 'חסכי {percent}%',
    'cart.quickAdd': 'הוסיפי',

    'promo.b2g1': '🎁 הוסיפי 3 פריטים לעגלה — 1 מהם חינם!',
    'promo.b2g1Cart': '🎁 מבצע 2+1 הופעל! על כל 3 פריטים 1 חינם!',
    'promo.b2g1CartHint': 'הוסיפי עוד {count} כדי לקבל מוצר חינם! (צריך 3 פריטים בעגלה)',
    'promo.b2g1Saving': 'חוסכת {amount} — הפריט הזול ביניהם חינם!',
    'promo.freeItem': '🎁 חינם במבצע',

    'about.title': 'אודות',
    'about.subtitle': 'המדע מאחורי יופי קוריאני',
    'about.pdrnTitle': 'המדע של טיפוח PDRN',
    'about.pdrnDescription': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מרכיב מהפכני בטיפוח הקוריאני. מוצרי הטיפוח PDRN שלנו מקדמים התחדשות תאית לאפקט Glass Skin מושלם.',
    'about.glassSkinDesc': 'השיגו את אפקט ה-Glass Skin הקוריאני המבוקש עם זוהר מיידי.',
    'about.hydrationDesc': 'לחות עמוקה שנמשכת כל היום, עם שימור לחות מוכח קלינית.',
    'about.antiAgingDesc': 'הגבירו באופן טבעי את ייצור הקולגן לעור מתוח וצעיר יותר.',

    'footer.copyright': '© 2026 PDRN SKIN. כל הזכויות שמורות.',
    'footer.freeShipping': 'משלוח חינם מעל {amount} (ישראל)',
    'footer.securePayment': 'תשלום מאובטח',
    'footer.support': 'תמיכה 24/7',
    'footer.description': 'טיפוח קוריאני פרימיום בטכנולוגיית PDRN מהפכנית. חוו את אפקט ה-Glass Skin עם המוצרים המדעיים שלנו.',
    'footer.sheetMasks': 'מסכות בד',
    'footer.serum': 'סרום',
    'footer.cream': 'קרם',
    'footer.bundle': 'סט מלא',
    'footer.faq': 'שאלות נפוצות',
    'footer.shippingReturns': 'משלוחים והחזרות',
    'footer.privacyPolicy': 'מדיניות פרטיות',
    'footer.termsOfService': 'תנאי שימוש',
    'footer.returnPolicy': 'מדיניות החזרות',
    'footer.contactSupport': 'אנו זמינים עבורכם לכל שאלה, בירור בנוגע לסטטוס הזמנה, בעיה או כל פנייה אחרת, או עזרה עם רכישה. צרו קשר עם שירות הלקוחות שלנו במייל',

    'common.verified': 'מאומת',
    'common.before': 'לפני',
    'common.after': 'אחרי',
    'common.loading': 'טוען...',
    'common.error': 'אירעה שגיאה',
    'common.noPhotoShared': 'הלקוחה לא צירפה תמונה',

    'media.videoPlaceholder': 'מציין מקום לסרטון',
    'media.gifPlaceholder': 'מציין מקום ל-GIF',
    'media.imagePlaceholder': 'מציין מקום לתמונה',

    'feature.hydration': '+100% לחות',
    'feature.glassSkin': 'אפקט Glass Skin',
    'feature.antiAging': 'אנטי-אייג\'ינג',
    'feature.collagen': 'בוסט קולגן',
    'feature.brightening': 'הבהרה',

    'product.howToUse': 'אופן השימוש',
    'product.ingredients': 'מרכיבים עיקריים',
    'product.reviews': 'ביקורות לקוחות',
    'product.results': 'תוצאות אמיתיות',
    'product.realResults': 'תוצאות אמיתיות',
     'product.beforeAfter': 'לפני ואחרי',
     'product.faqTitle': 'שאלות ותשובות',
     'product.mediaGallery': 'גלריית מדיה',
    'product.freeShipping': 'משלוח חינם מ-{amount}',
    'product.secureCheckout': 'תשלום מאובטח',
    'product.returns': 'החזרה תוך 7 ימים',
    'product.koreanFormula': 'פורמולה קוריאנית',
    'product.premiumIngredients': 'רכיבים פרימיום',
    'product.backHome': 'חזרה לדף הבית',
    'product.notFound': 'המוצר לא נמצא',
    'product.bestValue': 'הכי משתלם',
    'product.bestseller': 'רב מכר',
    'product.reviewCount': '({count} ביקורות)',
    'product.crueltyFree': 'ללא ניסויים בבעלי חיים',
    'product.youMayAlsoLike': 'אולי תאהבו גם',
    'product.completeRoutine': 'השלימו את שגרת הטיפוח שלכם',
    'product.moneyBack': 'החזר כספי תוך 7 ימים',
    'product.dermatologistTested': 'נבדק דרמטולוגית',
    'product.payWith': 'תשלום מאובטח באמצעות',
    'product.specsTitle': 'פרטי המוצר',
    'product.specsVolume': 'גודל',
    'product.specsSkinType': 'סוג עור',
    'product.specsTested': 'בדיקות',
    'product.specsClinicallyTested': 'נבדק קלינית',
    'product.specsShelfLife': 'תוקף',
    'product.specsFreeOf': 'ללא פרבנים, סולפטים ופתלטים',
    'product.satisfactionGuarantee': 'ערבות 100% שביעות רצון',
    'product.satisfactionDesc': 'נסי ללא סיכון עם הערבות שלנו להחזר כספי תוך 7 ימים',

    'beforeAfter.customer1': 'חנה',
    'beforeAfter.customer2': 'אניטה',
    'beforeAfter.customer3': 'לטניישה',
    'beforeAfter.weeks3': '3 שבועות שימוש',
    'beforeAfter.weeks8': '8 שבועות שימוש',
    'beforeAfter.weeks12': '12 שבועות שימוש',

    'mobileCart.findRoutine': 'מצאי את השגרה שלך',

    'survey.buttonText': 'מצאי את השגרה שלך',
    'survey.title': 'אבחון עור',
    'survey.subtitle': 'עני על כמה שאלות קצרות ואני אתאים לך את השגרה המושלמת',
    'survey.question': 'שאלה',
    'survey.back': 'חזרה',
    'survey.next': 'הבא',
    'survey.getResults': 'לתוצאות',
    'survey.resultsTitle': 'השגרה שהתאמתי לך',
    'survey.resultsSubtitle': 'בהתבסס על מה שסיפרת לי, הנה מה שאני ממליצה לך',
    'survey.yourProfile': 'פרופיל העור שלך',
    'survey.recommended': 'מומלץ עבורך',
    'survey.bundleNote': 'לתוצאות הכי טובות, אני ממליצה לך על הסט המלא',
    'survey.howToUse': 'השגרה שהתאמתי לך',
    'survey.morning': 'בוקר',
    'survey.evening': 'ערב',
    'survey.expectedResults': 'תוצאות צפויות',
    'survey.week': 'שבוע',
    'survey.weeks': 'שבועות',
    'survey.personalTips': 'טיפים שלי עבורך',
    'survey.retake': 'מלאי שוב',
    'survey.shopNow': 'קני עכשיו',
    'survey.errorTooMany': 'יותר מדי בקשות. נסי שוב מאוחר יותר.',
    'survey.errorGeneric': 'משהו השתבש. נסי שוב.',
    'survey.yourPersonalizedRoutine': 'השגרה שהתאמתי לך',
    'survey.treatmentPlan': 'משך טיפול מומלץ',
    'survey.addRoutineToCart': 'הוסיפי את השגרה לסל',
    'survey.satisfactionGuarantee': 'אחריות 100% שביעות רצון',
    'survey.maybeLater': 'אולי מאוחר יותר',
    'survey.continueSurvey': 'המשיכי בשאלון',
    'survey.addedToCart': 'המוצרים נוספו לסל!',
    'survey.personalizedRoutine': 'השגרה שהתאמתי לך מוכנה',
    'survey.errorAddingToCart': 'שגיאה בהוספה לסל',
    'survey.exclusiveDiscount': '10% הנחה בלעדית בשבילך!',
    'survey.couponCopied': 'קוד הקופון הועתק!',
    'survey.useAtCheckout': 'השתמשי בקוד SURVEY10 בתשלום לקבלת 10% הנחה',
    'survey.bestDeal': 'העסקה הכי טובה שמצאתי לך!',
    'survey.separatePrice': 'בנפרד',
    'survey.yourPrice': 'המחיר שלך',
    'survey.youSave': 'את חוסכת',
    'survey.upgradeOption': 'רוצה תוצאות טובות עוד יותר?',
    'survey.upgradeToBundle': 'שדרוג לשגרה מלאה',
    'survey.upgradeSave': 'חסכי {amount} עם הסט המלא',
    'survey.addSingleToCart': 'הוסיפי לסל',

    // Survey loading messages
    'survey.loading.analyzing': 'מנתחת את פרופיל העור שלך...',
    'survey.loading.planning': 'מתכננת טיפול מותאם אישית...',
    'survey.loading.matching': 'מאתרת את המוצרים המושלמים עבורך...',
    'survey.loading.finalizing': 'כמעט מוכן...',

    'survey.q1.question': 'מהו סוג העור שלך?',
    'survey.q1.dry': 'יבש - העור שלי מרגיש מתוח לעיתים קרובות',
    'survey.q1.oily': 'שמנוני - נוצץ, בעיקר במצח ובאף',
    'survey.q1.combination': 'משולב - מצח ואף שמנוניים, לחיים יבשות',
    'survey.q1.normal': 'רגיל - מאוזן, ללא בעיות משמעותיות',
    'survey.q2.question': 'מה הדבר שהכי מפריע לך בעור?',
    'survey.q2.aging': 'קמטים וקווי ביטוי',
    'survey.q2.acne': 'אקנה ופצעונים',
    'survey.q2.dullness': 'עור עמום וגוון לא אחיד',
    'survey.q2.hyperpigmentation': 'כתמים כהים והיפרפיגמנטציה',
    'survey.q3.question': 'כמה רגיש העור שלך?',
    'survey.q3.very': 'רגיש מאוד - מגיב לרוב המוצרים',
    'survey.q3.somewhat': 'רגיש במידה מסוימת - תגובות מדי פעם',
    'survey.q3.not': 'לא רגיש - סובל רוב המוצרים',
    'survey.q4.question': 'כמה לחות יש בעור שלך?',
    'survey.q4.veryDry': 'יבש מאוד - צריך לחות כל הזמן',
    'survey.q4.somewhatDry': 'קצת יבש - צריך לחות באופן קבוע',
    'survey.q4.balanced': 'מאוזן - לא יבש ולא שמנוני',
    'survey.q4.wellHydrated': 'לחותי - מוצק ובריא',
    'survey.q5.question': 'מה טווח הגיל שלך?',
    'survey.q5.under25': 'מתחת ל-25',
    'survey.q5.25to35': '25-35',
    'survey.q5.35to45': '35-45',
    'survey.q5.over45': 'מעל 45',
    'survey.q6.question': 'איך את מתארת את שגרת הטיפוח הנוכחית שלך?',
    'survey.q6.minimal': 'מינימלית - רק ניקוי ולחות',
    'survey.q6.basic': 'בסיסית - ניקוי, טונר, לחות',
    'survey.q6.moderate': 'בינונית - כולל סרומים וטיפולים',
    'survey.q6.extensive': 'מקיפה - שגרת טיפוח קוריאנית מלאה',
    'survey.q7.question': 'איך את מתארת את מרקם העור שלך?',
    'survey.q7.smooth': 'חלק ואחיד',
    'survey.q7.rough': 'מחוספס ובעל מרקם',
    'survey.q7.uneven': 'לא אחיד עם כמה בליטות',
    'survey.q7.bumpy': 'גבשושי עם נקבוביות בולטות',
    'survey.q8.question': 'מה המטרה העיקרית שלך בטיפוח?',
    'survey.q8.glow': 'להשיג את אפקט הזוהר הקוריאני',
    'survey.q8.antiAging': 'להפחית סימני הזדקנות',
    'survey.q8.hydration': 'לחות עמוקה',
    'survey.q8.all': 'כל הנ"ל',

    // Trust Strip
    'trust.clinicallyTested': 'נבדק קלינית',
    'trust.happyCustomers': '10,000+ לקוחות מרוצות',
    'trust.labFormula': 'פורמולת מעבדה קוריאנית',
    'trust.moneyBack': 'החזר כספי תוך 7 ימים',

    'science.title': 'PDRN מ-DNA סלמון — מגובה מדעית',
    'science.subtitle': 'PDRN מוכח קלינית עם מחקרים שעברו ביקורת עמיתים',
    'science.citation1': '"PDRN מקדם ריפוי פצעים ורגנרציה של רקמות דרך הפעלת קולטן אדנוזין A2A."',
    'science.source1': '— Journal of Cosmetic Dermatology, 2020',
    'science.citation2': '"PDRN ממקור סלמון משפר משמעותית לחות, גמישות ומפחית עומק קמטים בניסויים קליניים."',
    'science.source2': '— International Journal of Molecular Sciences, 2021',

    'scienceInside.title': 'המדע שבפנים',
    'scienceInside.subtitle': 'למה הפורמולה הזו עובדת — שני מנועים עוצמתיים בסרום אחד',
    'scienceInside.peptides.title': 'פפטידים — המהנדסים',
    'scienceInside.peptides.subtitle': '"שליחים חכמים, לא קרם קסמים"',
    'scienceInside.peptides.point1': 'שרשראות קצרות של חומצות אמינו שמסמנות לעור שלך להתנהג כמו עור צעיר ומתחדש',
    'scienceInside.peptides.point2': 'תמיכה במראה מוצק יותר וקמטוטים פחות בולטים לאורך זמן',
    'scienceInside.peptides.point3': 'מחזקים את מחסום העור — העור מרגיש פחות "שביר" ופחות תגובתי',
    'scienceInside.peptides.point4': 'אפקט חכם שמתעצם בשימוש עקבי, לא נס של יום אחד',
    'scienceInside.pdrn.title': 'PDRN — המשקם',
    'scienceInside.pdrn.subtitle': 'רכיב השיקום המוביל בעולם ה-K-Beauty',
    'scienceInside.pdrn.point1': 'מקטעי DNA שמדברים בשפה של התחדשות ושיקום',
    'scienceInside.pdrn.point2': 'תומך באיכות העור ומקדם מראה בריא וצפוף',
    'scienceInside.pdrn.point3': 'מרגיע עור סטרס או תגובתי — פחות אדמומיות, יותר איזון',
    'scienceInside.pdrn.point4': 'בסרום: שיפור עקבי וחכם — לא דרמטי בן לילה',
    'scienceInside.combo.title': 'שני מנועים, סרום אחד',
    'scienceInside.combo.subtitle': 'גלאו מיידי + אנטי-אייג\'ינג מצטבר',
    'scienceInside.combo.point1': 'מנוע 1: "רואים עכשיו" — לחות, פיזור אור, מרקם חלק יותר',
    'scienceInside.combo.point2': 'מנוע 2: "רואים עם הזמן" — מיצוק, אלסטיות, שיקום',
    'scienceInside.combo.point3': 'ה-"וואו" המיידי גורם לך להתמיד — הפפטידים וה-PDRN עושים את השאר',
    'scienceInside.combo.point4': 'התוצאה: עור שנראה צפוף, רגוע ומלוטש',
    'scienceInside.tagline': 'פפטידים בונים ומחזקים. PDRN מתקן ומרגיע. יחד, הם נותנים לעור שלך גם זוהר מיידי וגם התחדשות לאורך זמן.',

    // Science Inside — Default (fallback למוצרים חדשים)
    'scienceInside.default.subtitle': 'המדע מאחורי PDRN — איך הוא משנה את העור שלך מבפנים',
    'scienceInside.default.tagline': 'PDRN מפעיל את מנגנון התיקון הטבעי של העור, מקדם התחדשות תאית ולחות עמוקה כבר מהשימוש הראשון.',

    // Default product FAQ
    'productContent.default.faq1.q': 'מה זה PDRN ואיך זה עובד?',
    'productContent.default.faq1.a': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון. הוא מפעיל את מנגנון התיקון הטבעי של העור דרך קולטן האדנוזין A2A, ומקדם התחדשות תאית, ייצור קולגן ולחות עמוקה.',
    'productContent.default.faq2.q': 'האם המוצר מתאים לעור רגיש?',
    'productContent.default.faq2.a': 'כן! הפורמולות שלנו נבדקו דרמטולוגית, ללא אכזריות, וללא פרבנים, סולפטים ופתלטים. הן מתוכננות להיות עדינות על כל סוגי העור, כולל עור רגיש.',
    'productContent.default.faq3.q': 'כמה זמן עד שרואים תוצאות?',
    'productContent.default.faq3.a': 'רוב המשתמשות מבחינות בלחות משופרת וזוהר נראה כבר מהשימוש הראשון. לתוצאות מיטביות בקווי ביטוי ובמרקם העור, מומלץ שימוש עקבי של 4-8 שבועות.',

    // Science Inside — Masks
    'scienceInside.masks.subtitle': 'איך שלושה מרכיבים עוצמתיים עובדים יחד בכל מסכת בד',
    'scienceInside.masks.tagline': 'PDRN מתקן ברמה התאית. ויטמין B12 מאיר. ניאצינמייד מחזק. 20 דקות — זה כל מה שהעור שלך צריך כדי לספוג את הכל.',
    'scienceInside.masks.card1.title': 'PDRN + ויטמין B12 — הצמד המאיר',
    'scienceInside.masks.card1.subtitle': 'תיקון תאי פוגש זוהר נראה',
    'scienceInside.masks.card1.point1': 'PDRN מפעיל התחדשות תאית בזמן שויטמין B12 מעניק אפקט הבהרה מיידי',
    'scienceInside.masks.card1.point2': 'יחד הם מטפלים בעמימות במקור — לא רק זוהר שטחי',
    'scienceInside.masks.card1.point3': 'B12 הוא חומר מטפח עור מוכח שמאחד את גוון העור לאורך זמן',
    'scienceInside.masks.card1.point4': 'הצמד עובד בסינרגיה — PDRN מחדש, B12 מאיר',
    'scienceInside.masks.card2.title': 'ניאצינמייד — בונה המחסום',
    'scienceInside.masks.card2.subtitle': 'מחזק, מאזן, מגן',
    'scienceInside.masks.card2.point1': 'מחזק את מחסום העור כדי לנעול לחות ולמנוע כניסת מגרים',
    'scienceInside.masks.card2.point2': 'מצמצם נקבוביות באופן נראה ומווסת עודפי שומן',
    'scienceInside.masks.card2.point3': 'מפחית אדמומיות ומרגיע דלקות — אידיאלי לעור רגיש',
    'scienceInside.masks.card2.point4': 'עובד בכל שכבות העור לשיפור מרקם, גוון ועמידות',
    'scienceInside.masks.card3.title': 'אפקט המסכה — 20 דקות של ספיגה עמוקה',
    'scienceInside.masks.card3.subtitle': 'למה מסכת בד עדיפה על סרום רגיל',
    'scienceInside.masks.card3.point1': 'מסכת הבד יוצרת סביבה אטומה שמונעת התאדות',
    'scienceInside.masks.card3.point2': 'מרכיבים פעילים מסופקים בריכוז גבוה יותר מסרומים יומיומיים',
    'scienceInside.masks.card3.point3': 'ספיגה עמוקה — המרכיבים חודרים עמוק יותר הודות למגע ממושך עם העור',
    'scienceInside.masks.card3.point4': 'תוצאות מיידיות ונראות: עור מלא יותר, לחותי יותר וזוהר כבר אחרי שימוש אחד',

    // Science Inside — Cream
    'scienceInside.cream.subtitle': 'שלוש טכנולוגיות מתקדמות שעובדות בלילה כדי לשנות את העור שלך',
    'scienceInside.cream.tagline': 'קולגן בונה מחדש מבנה. צרמידים מגנים על המחסום. קפסולות חכמות מספקות הכל לאורך הלילה — כדי שתתעוררי עם עור מחודש ומוצק.',
    'scienceInside.cream.card1.title': 'קולגן הידרוליזה — המבנה',
    'scienceInside.cream.card1.subtitle': 'בנייה מחדש מבפנים החוצה',
    'scienceInside.cream.card1.point1': 'מפורק לשברים קטנים שיכולים באמת לחדור לעור — לא רק לשבת על פני השטח',
    'scienceInside.cream.card1.point2': 'תומך במטריצת המבנה של העור למוצקות וגמישות משופרות',
    'scienceInside.cream.card1.point3': 'עוזר להפחית את מראה הקמטים הדקים על ידי מילוי מבפנים',
    'scienceInside.cream.card1.point4': 'עובד עם ייצור הקולגן הטבעי של העור שלך — חיזוק, לא החלפה',
    'scienceInside.cream.card2.title': 'קומפלקס צרמידים — המגן',
    'scienceInside.cream.card2.subtitle': 'ההגנה הטבעית של העור שלך, מחוזקת',
    'scienceInside.cream.card2.point1': 'צרמידים מהווים 50% ממחסום העור הטבעי — אנחנו משלימים מה שאבד עם הגיל',
    'scienceInside.cream.card2.point2': 'נועל לחות עד 72 שעות — בלי עוד תחושת יובש ומתיחות בלילה',
    'scienceInside.cream.card2.point3': 'מגן מפני נזק סביבתי: זיהום, אוויר יבש, שינויי טמפרטורה',
    'scienceInside.cream.card2.point4': 'מחזק את המחסום כדי שמרכיבים פעילים אחרים יעבדו בצורה יעילה יותר',
    'scienceInside.cream.card3.title': 'טכנולוגיית קפסולות חכמות — שחרור מדורג',
    'scienceInside.cream.card3.subtitle': 'מרכיבים פעילים מסופקים לאורך כל הלילה',
    'scienceInside.cream.card3.point1': 'מיקרו-קפסולות מתפרקות בהדרגה ומשחררות מרכיבים פעילים במשך 8 שעות',
    'scienceInside.cream.card3.point2': 'מונע פירוק מרכיבים — הם נשארים חזקים עד הרגע שהם משתחררים',
    'scienceInside.cream.card3.point3': 'העור שלך מקבל זרם קבוע של הזנה במקום מנה אחת גדולה שדועכת',
    'scienceInside.cream.card3.point4': 'התעוררי לעור מחודש ולחותי — הקפסולות עשו את העבודה בזמן שישנת',

    // Science Inside — Bundle
    'scienceInside.bundle.subtitle': 'למה שלושה מוצרים יחד מספקים תוצאות שאף מוצר בודד לא יכול',
    'scienceInside.bundle.tagline': 'מסכות מנקות ומזינות. הסרום מתקן ומאיר. הקרם נועל הכל בפנים. שלושה שלבים, מערכת אחת — 24 שעות של שינוי עור רציף.',
    'scienceInside.bundle.card1.title': 'שלושה שלבים, מערכת אחת',
    'scienceInside.bundle.card1.subtitle': 'כל מוצר מכין את העור לשלב הבא',
    'scienceInside.bundle.card1.point1': 'שלב 1: מסכות בד מנקות לעומק ומזינות PDRN מרוכז + ויטמין B12',
    'scienceInside.bundle.card1.point2': 'שלב 2: הסרום מתקן, ממצק ומספק את אפקט ה-Glass Skin',
    'scienceInside.bundle.card1.point3': 'שלב 3: הקרם נועל הכל עם קולגן, צרמידים וקפסולות שחרור מדורג',
    'scienceInside.bundle.card1.point4': 'יחד: שגרה מלאה שבה כל שלב מעצים את הקודם',
    'scienceInside.bundle.card2.title': 'אפקט הסינרגיה — טוב יותר ביחד',
    'scienceInside.bundle.card2.subtitle': 'תוצאות מצטברות שמתעצמות עם הזמן',
    'scienceInside.bundle.card2.point1': 'שימוש בשלושת המוצרים יוצר אפקט מצטבר — התוצאות בונות אחת על השנייה',
    'scienceInside.bundle.card2.point2': 'PDRN בכל שלב אומר התחדשות תאית עקבית לאורך כל השגרה',
    'scienceInside.bundle.card2.point3': 'שיטות מתן שונות (מסכה, סרום, קרם) מבטיחות שהמרכיבים מגיעים לכל שכבות העור',
    'scienceInside.bundle.card2.point4': 'לקוחות מדווחות על תוצאות מהירות יותר באופן משמעותי עם השגרה המלאה',
    'scienceInside.bundle.card3.title': 'כיסוי 24 שעות — יום ולילה',
    'scienceInside.bundle.card3.subtitle': 'העור שלך אף פעם לא נשאר ללא הגנה',
    'scienceInside.bundle.card3.point1': 'בוקר: סרום מתחת לאיפור ללחות, הגנה וזוהר כל היום',
    'scienceInside.bundle.card3.point2': 'ערב: מסכת בד לניקוי עמוק והזנה (2-3 פעמים בשבוע)',
    'scienceInside.bundle.card3.point3': 'לילה: קרם עם קפסולות שחרור מדורג שעובדות בזמן שאת ישנה',
    'scienceInside.bundle.card3.point4': 'התוצאה: התחדשות מסביב לשעון — העור שלך משתפר 24/7',

    // What is PDRN (educational section)
    'whatIsPDRN.title': 'מה זה PDRN?',
    'whatIsPDRN.subtitle': 'המרכיב המהפכני שמשנה את עולם הטיפוח — מהקליניקה הביתה',
    'whatIsPDRN.origin.title': 'מופק מ-DNA של סלמון',
    'whatIsPDRN.origin.subtitle': 'מרכיב מוכח ברפואה אסתטית',
    'whatIsPDRN.origin.point1': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות ברחבי העולם',
    'whatIsPDRN.origin.point1.US': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי ארצות הברית',
    'whatIsPDRN.origin.point1.GB': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי בריטניה',
    'whatIsPDRN.origin.point1.AU': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי אוסטרליה',
    'whatIsPDRN.origin.point1.IL': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי ישראל',
    'whatIsPDRN.origin.point1.FR': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי צרפת',
    'whatIsPDRN.origin.point1.DE': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי גרמניה',
    'whatIsPDRN.origin.point1.ES': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי ספרד',
    'whatIsPDRN.origin.point1.NO': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי נורווגיה',
    'whatIsPDRN.origin.point1.HR': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי קרואטיה',
    'whatIsPDRN.origin.point1.FI': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי פינלנד',
    'whatIsPDRN.origin.point1.AE': 'PDRN (פולידיאוקסיריבונוקלאוטיד) מופק מ-DNA של סלמון — מולקולה ביו-תואמת שמשמשת בקליניקות דרמטולוגיות בכל רחבי איחוד האמירויות',
    'whatIsPDRN.origin.point2': 'מגובה במחקרים קליניים עם תוצאות מדידות בלחות ואלסטיות',
    'whatIsPDRN.origin.point3': 'תרכובת רגנרטיבית טבעית — ללא מילויים סינתטיים, ללא כימיקלים קשים',
    'whatIsPDRN.howItWorks.title': 'רגנרציה ברמה התאית',
    'whatIsPDRN.howItWorks.subtitle': 'פועל היכן שמוצרים אחרים לא מגיעים',
    'whatIsPDRN.howItWorks.point1': 'מפעיל את קולטן האדנוזין A2A — מעורר את תהליך התיקון וההתחדשות הטבעי של העור שלך',
    'whatIsPDRN.howItWorks.point2': 'מקדם ייצור קולגן ואלסטין מבפנים, לא רק לחות שטחית',
    'whatIsPDRN.howItWorks.point3': 'מפחית דלקות ומרגיע עור סטרס לעור בריא ומאוזן יותר',
    'whatIsPDRN.results.title': 'תוצאות ברמה קלינית בבית',
    'whatIsPDRN.results.subtitle': 'עד לאחרונה — רק בקליניקות',
    'whatIsPDRN.results.point1': 'עד לאחרונה, טיפולי PDRN היו זמינים רק בקליניקות דרמטולוגיה יוקרתיות במחירים גבוהים',
    'whatIsPDRN.results.point2': 'הפורמולות שלנו מספקות את אותו מרכיב פעיל בפורמט לשימוש יומיומי שתוכלי למרוח בבית',
    'whatIsPDRN.results.point3': '+100% שיפור בלחות נמדד קלינית — שיפור נראה כבר מהמריחה הראשונה',

    // Proven Results
    'provenResults.title': 'תוצאות מוכחות',
    'provenResults.stat1.value': '+100%',
    'provenResults.stat1.label': 'שיפור בלחות',
    'provenResults.stat1.sub': 'נמדד קלינית',
    'provenResults.stat2.value': '93%',
    'provenResults.stat2.label': 'עור חלק יותר',
    'provenResults.stat2.sub': 'דווח תוך 4 שבועות',
    'provenResults.stat3.value': '4.9/5',
    'provenResults.stat3.label': 'דירוג לקוחות',
    'provenResults.stat3.sub': 'ממוצע בכל המוצרים',
    'provenResults.stat4.value': '+10,000',
    'provenResults.stat4.label': 'לקוחות מרוצות',
    'provenResults.stat4.sub': 'ברחבי העולם',
    'provenResults.stat4.sub.US': 'בכל רחבי ארצות הברית',
    'provenResults.stat4.sub.GB': 'בכל רחבי בריטניה',
    'provenResults.stat4.sub.AU': 'בכל רחבי אוסטרליה',
    'provenResults.stat4.sub.IL': 'בכל רחבי ישראל',
    'provenResults.stat4.sub.FR': 'בכל רחבי צרפת',
    'provenResults.stat4.sub.DE': 'בכל רחבי גרמניה',
    'provenResults.stat4.sub.ES': 'בכל רחבי ספרד',
    'provenResults.stat4.sub.NO': 'בכל רחבי נורווגיה',
    'provenResults.stat4.sub.HR': 'בכל רחבי קרואטיה',
    'provenResults.stat4.sub.FI': 'בכל רחבי פינלנד',
    'provenResults.stat4.sub.AE': 'בכל רחבי איחוד האמירויות',

    // Comparison Table
    'comparison.title': 'PDRN לעומת טיפוח רגיל',
    'comparison.headerRegular': 'טיפוח רגיל',
    'comparison.headerPDRN': 'PDRN SKIN',
    'comparison.row1.criteria': 'פעולה',
    'comparison.row1.regular': 'לחות שטחית',
    'comparison.row1.pdrn': 'רגנרציה ברמה התאית',
    'comparison.row2.criteria': 'מרכיב פעיל',
    'comparison.row2.regular': 'פפטידים גנריים',
    'comparison.row2.pdrn': 'PDRN (DNA סלמון)',
    'comparison.row3.criteria': 'תוצאות',
    'comparison.row3.regular': 'שבועות עד שרואים',
    'comparison.row3.pdrn': 'נראה כבר מהשימוש הראשון',
    'comparison.row4.criteria': 'טכנולוגיה',
    'comparison.row4.regular': 'פורמולות מדף',
    'comparison.row4.pdrn': 'טכנולוגיה ברמה קלינית',
    'comparison.row5.criteria': 'הוכחה קלינית',
    'comparison.row5.regular': 'טענות שיווקיות',
    'comparison.row5.pdrn': 'נבדק קלינית +100% לחות',

    'faq.sectionTitle': 'שאלות נפוצות',
    '404.title': 'הדף לא נמצא',
    '404.subtitle': 'הדף שחיפשתם לא קיים או שהועבר.',
    '404.backHome': 'חזרה לדף הבית',
    '404.popularProducts': 'מוצרים פופולריים',

    'product.disclaimer': '*התמונות להמחשה בלבד. התוצאות עשויות להשתנות מאדם לאדם בהתאם לסוג העור ולשימוש. המוצר אינו מיועד לאבחון, טיפול או מניעה של מצבים רפואיים. מומלץ לבצע בדיקת רגישות באזור נסתר לפני שימוש ראשון מלא. במידה ויש רגישות, יש להפסיק את השימוש. במידה והרגישות חמורה, יש לפנות לרופא. הספק יכול לשנות את אריזת המוצר ואת צורתו מעת לעת; המוצר הינו אותו המוצר.',
    'product.shippingMarquee': 'משלוח חינם בהזמנות מעל {amount} לכל רחבי הארץ',
    'product.inStockReady': 'זמין במלאי למשלוח מיידי',

    // Terms agreement
    'product.termsAgreementBefore': 'בגלישה ובקניה באתר הנכם מסכימים ל',
    'product.termsLink': 'תנאי השימוש',
    'product.termsAgreementAfter': 'באתר',

    'announcement.freeShipping': '✨ משלוח חינם בהזמנות מעל {amount} לכל רחבי הארץ',
    'announcement.close': 'סגירה',
    'urgency.limitedOffer': 'הצעה מוגבלת',
    'urgency.endsIn': 'נגמר בעוד',

    // Blog
    'blog.title': 'בלוג',
    'blog.subtitle': 'מדריכי טיפוח מקצועיים, מדע PDRN וטיפים מהטיפוח הקוריאני',
    'blog.metaDescription': 'קראו את המדריכים המקצועיים שלנו על טיפוח PDRN, מדע DNA סלמון, שגרות Glass Skin וטיפים מהטיפוח הקוריאני.',
    'blog.readMore': 'קראו עוד',
    'blog.minRead': 'דק\' קריאה',
    'blog.badge': 'טיפוח מבוסס מדע',
    'blog.chip.antiAging': 'אנטי-אייג\'ינג',
    'blog.chip.hydration': 'לחות',
    'blog.chip.sensitiveSkin': 'עור רגיש',
    'blog.chip.nightRoutine': 'שגרת לילה',
    'blog.chip.ingredients': 'מרכיבים',
    'blog.chip.layering': 'שכבות',
    'blog.chip.glassSkin': 'Glass Skin',
    'blog.chip.pdrnScience': 'מדע PDRN',
    'blog.relatedArticles': 'מאמרים קשורים',
    'blog.recommendedProducts': 'מוצרים מומלצים',
    'blog.latestArticles': 'החדש בבלוג שלנו',
    'blog.latestSubtitle': 'מדריכי טיפוח PDRN מקצועיים וטיפים מהטיפוח הקוריאני',
    'blog.viewAll': 'לכל המאמרים',
    'blog.statsArticles': 'מאמרים',
    'blog.statsLanguages': 'שפות',
    'blog.statsVersions': 'גרסאות מתורגמות',
    'blog.availableIn': 'זמין ב-11 שפות',
    'blog.avgReadTime': 'זמן קריאה ממוצע 5 דק\'',

    // SEO meta (homepage)
    'seo.homeTitle': 'PDRN טיפוח עור | סרום DNA סלמון קוריאני, מסכות וקרם | Glass Skin',
    'seo.homeDescription': 'טיפוח PDRN עם טכנולוגיית DNA סלמון קוריאנית. סרום PDRN, מסכות בד וקרם קפסולות לאפקט Glass Skin. מוכח קלינית עם משלוח חינם.',

    // SEO product titles & descriptions
    'seo.productTitle.pink-glow-serum-with-pdrn-peptides': 'סרום PDRN עם DNA סלמון ופפטידים | טיפוח PDRN',
    'seo.productDescription.pink-glow-serum-with-pdrn-peptides': 'קנו סרום PDRN עם DNA סלמון ופפטידים. סרום קוריאני לאפקט Glass Skin שמגביר קולגן ומחליק קמטים. טיפוח PDRN עם משלוח חינם.',
    'seo.productTitle.pdrn-pink-sheet-masks-10-pack': 'מסכות בד PDRN 10 יחידות | מסכות קוריאניות DNA סלמון | טיפוח PDRN',
    'seo.productDescription.pdrn-pink-sheet-masks-10-pack': 'קנו מסכות בד PDRN ספוגות בסרום DNA סלמון. 10 מסכות קוריאניות לאפקט Glass Skin עם PDRN, ויטמין B12 וניאצינאמיד.',
    'seo.productTitle.smart-capsule-cream-with-collagen': 'קרם קפסולות PDRN עם קולגן | טיפוח קוריאני | טיפוח PDRN',
    'seo.productDescription.smart-capsule-cream-with-collagen': 'קנו קרם קפסולות PDRN עם קולגן וניאצינאמיד. קרם אנטי-אייג\'ינג קוריאני עם טכנולוגיית DNA סלמון ללחות עמוקה ומיצוק.',
    'seo.productTitle.complete-pdrn-trio-bundle': 'סט PDRN מלא — סרום, מסכות וקרם | טיפוח PDRN',
    'seo.productDescription.complete-pdrn-trio-bundle': 'קנו את סט הטיפוח PDRN המלא עם סרום, 10 מסכות וקרם. חסכו עד 53% על טיפוח קוריאני עם DNA סלמון.',

    // Internal linking
    'seo.explorePDRN': 'גלו את טיפוח PDRN',
    'seo.anchor.serumBenefits': 'יתרונות סרום PDRN',
    'seo.anchor.howPDRNWorks': 'איך PDRN עובד',
    'seo.anchor.masksVsTraditional': 'מסכות PDRN מול מסורתיות',
    'seo.anchor.glassSkinGuide': 'מדריך Glass Skin',
    'seo.anchor.koreanSecret': 'הסוד הקוריאני לטיפוח',
    'seo.anchor.pdrnVsRegular': 'PDRN מול טיפוח רגיל',
  },

  hr: croatianTranslations,
  fi: finnishTranslations,
  fr: frenchTranslations,
  de: germanTranslations,
  no: norwegianTranslations,
  es: spanishTranslations,
  ar: arabicTranslations,
};

// ── Store ────────────────────────────────────────────────────────────────────

interface LanguageStore {
  language: Language;
  currency: Currency;
  detectedCountry: string | null;
  manuallySet: boolean;
  setLanguage: (lang: Language) => void;
  setCurrency: (currency: Currency) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  isLanguageLocked: () => boolean;
  initializeFromLocation: () => Promise<void>;
}

// Timezone-based country detection (instant, no network)
function detectCountryFromTimezone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return null;

    const tzCountryMap: Record<string, string> = {
      // USA
      'America/New_York': 'US', 'America/Chicago': 'US', 'America/Denver': 'US',
      'America/Los_Angeles': 'US', 'America/Phoenix': 'US', 'America/Anchorage': 'US',
      'Pacific/Honolulu': 'US',
      // Canada
      'America/Toronto': 'CA', 'America/Vancouver': 'CA', 'America/Edmonton': 'CA',
      'America/Winnipeg': 'CA', 'America/Halifax': 'CA', 'America/St_Johns': 'CA',
      // UK
      'Europe/London': 'GB',
      // Australia
      'Australia/Sydney': 'AU', 'Australia/Melbourne': 'AU', 'Australia/Brisbane': 'AU',
      'Australia/Perth': 'AU', 'Australia/Adelaide': 'AU', 'Australia/Hobart': 'AU',
      'Australia/Darwin': 'AU',
      // Israel
      'Asia/Jerusalem': 'IL', 'Asia/Tel_Aviv': 'IL',
      // Croatia
      'Europe/Zagreb': 'HR',
      // Finland
      'Europe/Helsinki': 'FI',
      // France
      'Europe/Paris': 'FR',
      // Germany
      'Europe/Berlin': 'DE',
      // Norway
      'Europe/Oslo': 'NO',
      // Spain
      'Europe/Madrid': 'ES', 'Atlantic/Canary': 'ES',
      // UAE
      'Asia/Dubai': 'AE',
    };

    return tzCountryMap[tz] || null;
  } catch {
    return null;
  }
}

// IP-based fallback detection
async function detectCountryFromIP(): Promise<string | null> {
  // Check sessionStorage cache to avoid repeated calls (prevents 429 rate limiting)
  try {
    const cached = sessionStorage.getItem('ip-country');
    if (cached) return cached === 'unknown' ? null : cached;
  } catch { /* ignore */ }

  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: AbortSignal.timeout(3000),
    });
    if (response.ok) {
      const data = await response.json();
      const code = data.country_code || null;
      try { sessionStorage.setItem('ip-country', code || 'unknown'); } catch { /* ignore */ }
      return code;
    }
    try { sessionStorage.setItem('ip-country', 'unknown'); } catch { /* ignore */ }
  } catch {
    try { sessionStorage.setItem('ip-country', 'unknown'); } catch { /* ignore */ }
  }
  return null;
}

function getLocaleFromCountry(countryCode: string | null): { language: Language; currency: Currency } {
  if (countryCode && countryToLocale[countryCode]) {
    return countryToLocale[countryCode];
  }
  // Default to UK English and GBP for unrecognized countries
  return { language: 'en-GB', currency: 'GBP' };
}

// Countries where language is auto-locked (no switcher shown)
const LOCKED_COUNTRIES = ['US', 'GB', 'AU', 'IL', 'HR', 'FI', 'FR', 'DE', 'NO', 'ES', 'AE'];

// Returns the currencies available for a given detected country
export function getAvailableCurrencies(detectedCountry: string | null): Currency[] {
  if (detectedCountry === 'GB') return ['GBP'];
  if (detectedCountry === 'AU') return ['AUD'];
  if (detectedCountry === 'IL') return ['ILS'];
  if (['HR', 'FI', 'FR', 'DE', 'ES'].includes(detectedCountry || '')) return ['EUR'];
  if (detectedCountry === 'NO') return ['NOK'];
  if (detectedCountry === 'AE') return ['AED'];
  // US and all other countries: USD + CAD
  return ['USD', 'CAD'];
}

// Returns true if only one currency is available (switcher should be hidden)
export function isCurrencyLocked(detectedCountry: string | null): boolean {
  return getAvailableCurrencies(detectedCountry).length <= 1;
}

export const useLanguage = create<LanguageStore>()(
  persist(
    (set, get) => ({
      language: 'en-GB' as Language,
      currency: 'GBP' as Currency,
      detectedCountry: null as string | null,
      manuallySet: false,

      setLanguage: (lang: Language) => {
        set({ language: lang, manuallySet: true });
        document.documentElement.lang = lang;
        document.documentElement.dir = isRTL(lang) ? 'rtl' : 'ltr';
      },

      setCurrency: (currency: Currency) => {
        set({ currency, manuallySet: true });
      },

      t: (key: string, params?: Record<string, string | number>) => {
        const { language } = get();
        let text =
          translations[language]?.[key] ??
          legalTranslations[language]?.[key] ??
          productContentTranslations[language]?.[key] ??
          translations['en-US']?.[key] ??
          legalTranslations['en-US']?.[key] ??
          productContentTranslations['en-US']?.[key] ??
          key;

        if (params) {
          Object.entries(params).forEach(([k, v]) => {
            text = text.replace(`{${k}}`, String(v));
          });
        }

        return text;
      },

      isLanguageLocked: () => {
        const { detectedCountry } = get();
        return detectedCountry !== null && LOCKED_COUNTRIES.includes(detectedCountry);
      },

      initializeFromLocation: async () => {
        const { manuallySet } = get();

        // Check for ?lang= URL parameter (from hreflang / sitemap / shared links)
        try {
          const urlParams = new URLSearchParams(window.location.search);
          const langParam = urlParams.get('lang') as Language | null;
          if (langParam && languages.some(l => l.code === langParam)) {
            const langCurrencyMap: Record<string, Currency> = {
              'en-US': 'USD', 'en-GB': 'GBP', 'en-AU': 'AUD',
              'he': 'ILS', 'de': 'EUR', 'fr': 'EUR', 'es': 'EUR',
              'fi': 'EUR', 'hr': 'EUR', 'no': 'NOK', 'ar': 'AED',
            };
            const langCountryMap: Record<string, string> = {
              'en-US': 'US', 'en-GB': 'GB', 'en-AU': 'AU',
              'he': 'IL', 'de': 'DE', 'fr': 'FR', 'es': 'ES',
              'fi': 'FI', 'hr': 'HR', 'no': 'NO', 'ar': 'AE',
            };
            set({ language: langParam, currency: langCurrencyMap[langParam] || 'USD', detectedCountry: langCountryMap[langParam] || null, manuallySet: false });
            document.documentElement.lang = langParam;
            document.documentElement.dir = isRTL(langParam) ? 'rtl' : 'ltr';
            return;
          }
        } catch { /* ignore */ }

        if (manuallySet) {
          // User explicitly chose a language — respect it, just set HTML attrs
          const currentLang = get().language;
          document.documentElement.lang = currentLang;
          document.documentElement.dir = isRTL(currentLang) ? 'rtl' : 'ltr';
          return;
        }

        // Auto-detect: run every session when not manually set
        // 1. Try IP-based detection first (reflects actual network location, respects VPN)
        let countryCode = await detectCountryFromIP();

        // 2. Fallback to timezone-based detection if IP failed
        if (!countryCode) {
          countryCode = detectCountryFromTimezone();
        }

        const { language, currency } = getLocaleFromCountry(countryCode);

        set({
          language,
          currency,
          detectedCountry: countryCode,
          // manuallySet stays false — will re-detect next session
        });
        document.documentElement.lang = language;
        document.documentElement.dir = isRTL(language) ? 'rtl' : 'ltr';
      },
    }),
    {
      name: 'language-preference',
      // Version 8: default changed from en-US/USD to en-GB/GBP — force re-detect
      version: 8,
      migrate: () => ({
        language: 'en-GB' as Language,
        currency: 'GBP' as Currency,
        detectedCountry: null as string | null,
        manuallySet: false,
      }),
      partialize: (state) => ({
        language: state.language,
        currency: state.currency,
        detectedCountry: state.detectedCountry,
        manuallySet: state.manuallySet,
      }),
    },
  ),
);
